import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import type { TextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { TextBox } from "@tql/ui-components-react/kendo/inputs";
import { memo, useCallback, useEffect, useRef, useState } from "react";

type TextboxCellProps = GridCustomCellProps & {
	/** Optional display value formatter for non-edit mode */
	formatValue?: (value: string) => string;
	maxLength?: number;
	inputMode:
		| "text"
		| "numeric"
		| "search"
		| "none"
		| "tel"
		| "url"
		| "email"
		| "decimal"
		| undefined;
};

export const TextboxCell = memo((props: TextboxCellProps) => {
	const { formatValue, onChange, tdProps, isInEdit, maxLength, inputMode } =
		props;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Kendo Grid dataItem is typed as any
	const dataItem = props.dataItem;
	const field = props.field ?? "";

	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Dynamic field access from Kendo Grid
	const fieldValue = field ? String(dataItem[field] ?? "") : "";

	// Local state to prevent focus loss on every keystroke
	const [localValue, setLocalValue] = useState(fieldValue);
	// Use a ref for the latest value to keep handleBlur stable and avoid focus loss issues
	const valueRef = useRef(localValue);

	// Sync local state when fieldValue changes from outside (e.g., cancel edit)
	useEffect(() => {
		setLocalValue(fieldValue);
		valueRef.current = fieldValue;
	}, [fieldValue]);

	const handleChange = useCallback(
		(e: TextBoxChangeEvent) => {
			let value = e.value;

			if (inputMode === "numeric" && typeof value === "string") {
				value = value.replaceAll(/\D/g, "");
			}

			const stringValue = value == null ? "" : String(value);
			setLocalValue(stringValue);
			valueRef.current = stringValue;
		},
		[inputMode],
	);

	const handleBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			const currentLocalValue = valueRef.current;
			if (currentLocalValue !== fieldValue && onChange) {
				onChange({
					dataIndex: 0,
					// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Kendo Grid dataItem is typed as any
					dataItem,
					field,
					syntheticEvent: e,
					value: currentLocalValue,
				});
			}
		},
		[onChange, dataItem, field, fieldValue],
	);

	const displayValue = formatValue ? formatValue(fieldValue) : fieldValue;

	return (
		<td {...tdProps}>
			{isInEdit ? (
				<TextBox
					inputMode={inputMode}
					maxLength={maxLength}
					onBlur={handleBlur}
					onChange={handleChange}
					value={localValue}
				/>
			) : (
				displayValue
			)}
		</td>
	);
});

TextboxCell.displayName = "TextboxCell";
